<template>
    <div @click="triggerFileInput" :style="{ cursor: loading ? 'not-allowed' : 'pointer' }">
        <slot name="button">
            <!-- Default button if no slot provided -->
            <button
                class="btn-cover-upload"
                :disabled="loading"
            >
                <div v-if="loading" class="loading-spinner">
                    <i class="bi bi-arrow-repeat"></i>
                </div>
                <i v-else class="bi bi-card-image"></i>
                {{ loading ? $t('cover.loading') : displayButtonText }}
            </button>
        </slot>
    </div>
    <input
        ref="fileInput"
        type="file"
        accept="image/*"
        style="display: none"
        @change="handleFileChange"
    />
</template>

<script>
import storageService from '@/scripts/services/StorageService';

export default {
    name: 'CoverUpload',
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        previewSrc: {
            type: String,
            default: ''
        },
        buttonText: {
            type: String,
            default: ''
        }
    },
    emits: ['update:modelValue', 'cover-loaded', 'change-state'],
    data() {
        return {
            loading: false
        }
    },
    computed: {
        coverImageUrl() {
            if (this.previewSrc) {
                return this.previewSrc;
            }
            if (this.modelValue) {
                return this.modelValue;
            }
            return null;
        },
        displayButtonText() {
            return this.buttonText || this.$t('cover.upload')
        }
    },
    methods: {
        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        async handleFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (!file.type.startsWith('image/')) {
                alert(this.$t('cover.selectImage'));
                return;
            }

            console.log('Starting cover upload...');
            this.$emit('change-state', true);
            this.loading = true;

            try {
                // Store data URL locally, upload file in remote mode
                let uploadPayload = file;
                if (storageService.state.storageType === 'local') {
                    uploadPayload = await this.readFileAsDataUrl(file);
                }
                const coverId = await storageService.uploadFile(uploadPayload, file.name);
                console.log('Cover upload completed:', coverId);

                // Emit the new cover ID
                this.$emit('update:modelValue', coverId);
                this.$emit('change-state', false);
                this.$emit('cover-loaded', coverId);
            } catch (error) {
                console.error("Error uploading cover:", error);
                this.$emit('change-state', false);
                alert(this.$t('cover.uploadError'));
            } finally {
                this.loading = false;
                this.$emit('change-state', false);
                console.log('Loading state set to false');
                // Clear the input to allow selecting the same file again
                event.target.value = '';
            }
        },
        readFileAsDataUrl(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
                reader.readAsDataURL(file);
            });
        },

        async removeCover() {
            if (this.modelValue) {
                try {
                    await storageService.deleteCoverImage(this.modelValue);
                } catch (error) {
                    console.error("Error deleting cover:", error);
                }
            }
            this.$emit('update:modelValue', '');
        },

        async loadCoverImage() {
            if (this.modelValue && !this.previewSrc) {
                try {
                    const imageUrl = await storageService.getFile(this.modelValue);
                    if (imageUrl) {
                        this.$emit('cover-loaded', this.modelValue);
                    }
                } catch (error) {
                    console.error("Error loading cover image:", error);
                }
            }
        }
    }
}
</script>

<style scoped>
.btn-cover-upload {
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 0.5rem 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    transition: background-color 0.3s ease;
}

.btn-cover-upload:hover:not(:disabled) {
    background-color: #0056b3;
}

.btn-cover-upload:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

.btn-cover-upload i {
    font-size: 1rem;
}

.loading-spinner {
    animation: spin 1s linear infinite;
    display: inline-block;
}

.loading-spinner i {
    font-size: 1rem;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
